// The dispatch family (#4435, epic #4391): getting work ONTO a box. Two
// This file covers pre-dispatch judgement, backpressure, task assignment and box
// identity; `dispatch-rollout.ts` covers the self-update ROLLOUT state
// machine (12+ `runner.update_*` kinds plus the release ledger), because that
// chain shares one small render helper and would otherwise make this file
// exceed the 800-line budget on its own.
//
// ─── READING `unknown` PAYLOADS ──────────────────────────────────────────────
//
// `TerminalRow.input`/`.output` are `unknown` (wire/terminal.ts), and null on any
// row whose `visibleToMaintainer` is false — withheld by the SELECT, not by a
// mapper. `str`/`num`/`rec` below narrow without throwing; a renderer that reads
// a field the writer did not set gets `undefined` and degrades rather than
// crashes. Two write sites in this family (`task.assigned`, and the setup-lane
// half of `runner.capabilities_updated`) set `visibleToMaintainer: false`
// UNCONDITIONALLY (grepped both call sites, 2026-09-18) — so for those two kinds
// `row.input` is ALWAYS null in production and the renderer below is written
// to that fact, not to the writer's schema.
//
// ─── WHERE A WRITER WAS NOT FOUND ────────────────────────────────────────────
//
// `dispatch.escalated` has no call site that writes an `audit_events` row today:
// `buildDispatchEscalate` in `apps/api/src/deps/army.ts` only ever calls the
// structured-stdout `log()` with this kind, and its DURABLE row is
// `AuditEventKind.EscalationFired` — a different kind entirely. It renders
// defensively off ID-shaped fields only. (`plan.scoped` had no writer at all and
// is deleted with its renderer — plan 101 sweep 2026-10-01, finding 14-16.)

import type { TerminalRenderContext, TerminalRendered } from '../../../wire/terminal';
import { terminalRendererGroup } from '../../../wire/terminal';
import { AuditEventKind } from '../../audit';
import { ACCOUNT_VERBS_DISPATCH } from './account-verb';
import { dispatchModelEscalated } from './dispatch-escalation';
import {
  boxSubject,
  keyCooledLine,
  noCredentialLine,
  num,
  rec,
  str,
  strEither,
} from './dispatch-payload';
import { TERMINAL_RENDERERS_DISPATCH_ROLLOUT } from './dispatch-rollout';

/** A `task.*` / dispatch-router kind's subject: the task. `null` when unnamed. */
function taskSubject(taskId: string | undefined) {
  if (taskId === undefined) return null;
  return { kind: 'task' as const, id: taskId, label: taskId };
}

// ─── expectation.* (pre-action commitment, #26) ──────────────────────────────

const expectationRecorded = {
  level: 'info' as const,
  actor: 'coder' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const action = str(input, 'action');
    if (action === undefined) return null;
    return {
      line: { key: 'terminal.expectation.recorded', params: { action } },
      subject: { kind: 'session', id: ctx.row.sessionId, label: action },
      refs: { sessionId: ctx.row.sessionId },
    };
  },
};

const expectationChecked = {
  level: 'info' as const,
  actor: 'coder' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const output = rec(ctx.row.output);
    const expectationId = str(input, 'expectationId');
    if (expectationId === undefined) return null;
    const passed = input?.['passed'] === true;
    const accepted = output?.['accepted'] === true;
    return {
      line: {
        key: 'terminal.expectation.checked',
        params: {
          expectationId,
          passed: passed ? 'true' : 'false',
          accepted: accepted ? 'true' : 'false',
        },
      },
      subject: { kind: 'session', id: ctx.row.sessionId, label: expectationId },
      refs: { sessionId: ctx.row.sessionId },
    };
  },
};

// ─── plan.* (scout plan admission, #4009/#122) ───────────────────────────────

const planRefused = {
  level: 'warn' as const,
  actor: 'coder' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const output = rec(ctx.row.output);
    const taskId = str(input, 'taskId');
    const runId = str(input, 'runId');
    if (taskId === undefined) return null;
    const constraint = str(input, 'constraint') ?? 'unknown';
    const groupCount = num(input, 'groupCount') ?? 0;
    const message = str(output, 'message') ?? '';
    return {
      line: {
        key: 'terminal.plan.refused',
        params: { constraint, groupCount, message },
      },
      subject: { kind: 'task', id: taskId, label: taskId },
      refs: { taskId, ...(runId !== undefined ? { runId } : {}) },
    };
  },
};

/**
 * `plan.executed` / `plan.execute_refused` / `plan.revised` (`plan execute`,
 * `revised` #5130 §5): the subject is the PLAN, labelled by its directory.
 * `revised` reads `from_revision` / `to_revision` off `input` (§5:
 * `{ planId, repo, path, from_revision, to_revision, dryRun }` — `output` is
 * `revisePlanGroups`' own answer plus `released_task_count`, neither of which
 * names the plan or its revision pair) and renders nothing when either is
 * absent, the same posture the other two keys take on an unrecognised row —
 * a writer defect this renderer refuses to mask with a fallback.
 */
function planExecuteRenderer(
  level: 'info' | 'warn',
  key: 'executed' | 'execute_refused' | 'revised',
) {
  return {
    level,
    actor: 'system' as const,
    render(ctx: TerminalRenderContext): TerminalRendered | null {
      const input = rec(ctx.row.input);
      const output = rec(ctx.row.output);
      const planId = str(input, 'planId');
      if (planId === undefined) return null;
      const path = str(input, 'path') ?? planId;
      if (key === 'revised') {
        const fromRevision = num(input, 'from_revision');
        const toRevision = num(input, 'to_revision');
        if (fromRevision === undefined || toRevision === undefined) return null;
        return {
          line: {
            key: 'terminal.plan.revised' as const,
            params: { path, fromRevision, toRevision },
          },
          subject: { kind: 'plan', id: planId, label: path },
          refs: {},
        };
      }
      const groups = output?.['groups'];
      const params =
        key === 'executed'
          ? {
              path,
              phase: str(output, 'phase') ?? 'recorded',
              units: Array.isArray(groups) ? groups.length : 0,
            }
          : { path, reason: str(output, 'reason') ?? 'unknown' };
      return {
        line: { key: `terminal.plan.${key}` as const, params },
        subject: { kind: 'plan', id: planId, label: path },
        refs: {},
      };
    },
  };
}

/** `plan.file_requested` (#5129): a plan directory was filed inline — same
 * subject as `plan.executed`, labelled by its directory path, with the
 * filing kind carried so the terminal line can name an identical re-file
 * distinctly from a fresh first filing. */
const planFileRequested = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    // The real writer (`apps/api/src/deps/plan-file.ts`, #5129 §5) puts
    // `plan_id` / `path` / `filing` on `output`, never on `input` — `input`
    // carries what was ASKED (`repo`/`slug`/`documents`/`digest`/
    // `scoutTaskIds`/`dryRun`/`tokenId`). A refusal row's `output` is
    // `{ problems: kinds }`, so `planId` is undefined there and this renders
    // nothing for a refusal, same as it always has.
    const output = rec(ctx.row.output);
    const planId = str(output, 'plan_id');
    if (planId === undefined) return null;
    const path = str(output, 'path') ?? planId;
    // `filedRow` (`apps/api/src/deps/plan-file.ts`) ALWAYS sets `filing` on
    // a successful filing's audit row. A fallback string here would mask a
    // genuine writer defect rather than surface it, so an absent `filing`
    // renders nothing — the same posture the `planId` check above already
    // takes for a row this renderer does not recognize.
    const filing = str(output, 'filing');
    if (filing === undefined) return null;
    return {
      line: { key: 'terminal.plan.file_requested', params: { path, filing } },
      subject: { kind: 'plan', id: planId, label: path },
      refs: {},
    };
  },
};

const planGroupDropped = {
  level: 'warn' as const,
  actor: 'coder' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const taskId = str(input, 'taskId');
    const runId = str(input, 'runId');
    if (taskId === undefined) return null;
    const eligibleCount = num(input, 'eligibleCount') ?? 0;
    const mintedCount = num(input, 'mintedCount') ?? 0;
    return {
      line: {
        key: 'terminal.plan.group_dropped',
        params: { eligibleCount, mintedCount },
      },
      subject: { kind: 'task', id: taskId, label: taskId },
      refs: { taskId, ...(runId !== undefined ? { runId } : {}) },
    };
  },
};

// ─── dispatch.* (the router + backpressure) ──────────────────────────────────

const dispatchCreated = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    // Three writer shapes disagree on the task-id field name (`taskId` on the
    // GitHub-webhook and cron sites, plain `id` nowhere seen but guarded anyway).
    const taskId = strEither(input, 'taskId', 'id');
    if (taskId === undefined) return null;
    const repo = str(input, 'repo') ?? ctx.row.repoFullName ?? undefined;
    const prNumberVal = num(input, 'prNumber');
    return {
      line: {
        key: 'terminal.dispatch.created',
        params: { ...(repo !== undefined ? { repo } : {}) },
      },
      subject: { kind: 'task', id: taskId, label: repo ?? taskId },
      refs: {
        taskId,
        ...(ctx.row.repoFullName !== null ? { repoFullName: ctx.row.repoFullName } : {}),
        ...(prNumberVal !== undefined ? { prNumber: prNumberVal } : {}),
      },
    };
  },
};

/**
 * Written by `packages/agent/src/dispatch/task-dispatcher.ts`'s confidence
 * gate: `input` is the whitelisted envelope (carries `repo_full_name`, never
 * `threshold`), `output` is `{ escalation_id, threshold }` — the threshold
 * that tripped the escalation lives in `output`, not `input`. The escalation
 * id is the decision's own id and so the subject's; the repo is its label. A
 * row without one (withheld payload) falls back to the session subject — a
 * repo full name is not a decision id (#4445).
 */
const dispatchEscalated = {
  level: 'decision' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const output = rec(ctx.row.output);
    const repo = str(input, 'repo_full_name') ?? ctx.row.repoFullName ?? undefined;
    const threshold = num(output, 'threshold');
    if (repo === undefined && threshold === undefined) return null;
    const escalationId = str(output, 'escalation_id');
    const label = repo ?? 'dispatch escalated';
    return {
      line: {
        key: 'terminal.dispatch.escalated',
        params: { ...(threshold !== undefined ? { threshold } : {}) },
      },
      subject:
        escalationId !== undefined
          ? { kind: 'decision', id: escalationId, label }
          : { kind: 'session', id: ctx.row.sessionId, label },
      refs: { ...(ctx.row.repoFullName !== null ? { repoFullName: ctx.row.repoFullName } : {}) },
    };
  },
};

/**
 * `triage.verdict` — the dedup/confidence judge's decision (#829). The `output`
 * shape is `{ verdict: 'dupe-of'|'schedule'|'escalate'|'drop-noise', ... }`
 * (`packages/agent/src/dispatch/task-dispatcher.ts`); `input` is a whitelisted
 * envelope carrying no stable task id before a task is minted, so the subject
 * is the session this judgement ran in.
 */
const triageVerdict = {
  level: 'decision' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const output = rec(ctx.row.output);
    const verdict = str(output, 'verdict');
    if (verdict === undefined) return null;
    const dupeOf = str(output, 'dupe_of_task_id');
    return {
      line: {
        key: 'terminal.triage.verdict',
        params: { verdict, ...(dupeOf !== undefined ? { dupeOf } : {}) },
      },
      subject: { kind: 'session', id: ctx.row.sessionId, label: verdict },
      refs: { sessionId: ctx.row.sessionId, ...(dupeOf !== undefined ? { taskId: dupeOf } : {}) },
    };
  },
};

/**
 * `triage.info_answered` (plan 101 slice 08) — the asker answered a `needs_info`
 * question and the ask re-entered triage. Reads the raw task and blocker ids from
 * `input` in either casing, since the two answer doors (the blocker verbs and the
 * GitHub intake sink) share one writer but the row is read long after.
 */
const triageInfoAnswered = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const rawTaskId = strEither(input, 'raw_task_id', 'rawTaskId');
    const blockerId = strEither(input, 'blocker_id', 'blockerId');
    return {
      line: {
        key: 'terminal.triage.info_answered',
        params: rawTaskId !== undefined ? { rawTaskId } : {},
      },
      subject:
        rawTaskId !== undefined
          ? { kind: 'task', id: rawTaskId, label: rawTaskId }
          : { kind: 'session', id: ctx.row.sessionId, label: 'raw task' },
      refs: {
        ...(rawTaskId !== undefined ? { taskId: rawTaskId } : {}),
        ...(blockerId !== undefined ? { blockerId } : {}),
      },
    };
  },
};

const dispatchQueueFull = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const output = rec(ctx.row.output);
    const reason = str(output, 'reason') ?? 'queue_full';
    const queueDepth = num(output, 'queue_depth');
    const maxDepth = num(output, 'max_depth');
    return {
      line: {
        key: 'terminal.dispatch.queue_full',
        params: {
          reason,
          ...(queueDepth !== undefined ? { queueDepth } : {}),
          ...(maxDepth !== undefined ? { maxDepth } : {}),
        },
      },
      subject: { kind: 'session', id: ctx.row.sessionId, label: reason },
      refs: { sessionId: ctx.row.sessionId },
    };
  },
};

const dispatchSeatCapped = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const output = rec(ctx.row.output);
    const runnerId = str(input, 'runnerId');
    if (runnerId === undefined) return null;
    const cap = num(output, 'cap') ?? 0;
    const inFlight = num(output, 'inFlight') ?? 0;
    const held = num(output, 'held') ?? 0;
    return {
      line: { key: 'terminal.dispatch.seat_capped', params: { cap, inFlight, held } },
      subject: { kind: 'box', id: runnerId, label: runnerId },
      refs: { boxId: runnerId },
    };
  },
};

const dispatchSeatCapNotified = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const output = rec(ctx.row.output);
    const runnerId = str(input, 'runnerId');
    if (runnerId === undefined) return null;
    const queued = num(output, 'queued') ?? 0;
    return {
      line: { key: 'terminal.dispatch.seat_cap_notified', params: { queued } },
      subject: { kind: 'box', id: runnerId, label: runnerId },
      refs: { boxId: runnerId },
    };
  },
};

const dispatchSeatCapCleared = {
  level: 'ok' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const output = rec(ctx.row.output);
    const runnerId = str(input, 'runnerId');
    if (runnerId === undefined) return null;
    const cleared = num(output, 'cleared') ?? 0;
    return {
      line: { key: 'terminal.dispatch.seat_cap_cleared', params: { cleared } },
      subject: { kind: 'box', id: runnerId, label: runnerId },
      refs: { boxId: runnerId },
    };
  },
};

/**
 * `dispatch.no_credential` (#2846) — every `NoCredentialReason` arm
 * carry the reason's own `kind` on `output` verbatim (`apps/worker/src/wiring/
 * mothership.ts` spreads `reason` straight into `output`). Subject prefers the
 * task the claim was for; falls back to the box, since a lane-level exhaustion
 * (`no_pool`) may name neither a task nor a runner.
 */
const dispatchNoCredential = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const output = rec(ctx.row.output);
    const reasonKind = str(output, 'kind');
    if (reasonKind === undefined) return null;
    const lane = str(input, 'lane') ?? 'unknown';
    const taskId = str(input, 'taskId');
    const runnerId = str(input, 'runnerId');
    // Neither a task nor a box is always present (a lane-wide exhaustion like
    // `no_pool` names neither), so the fallback is the SESSION this row is on
    // — a real, routable id (`/audit/:session_id`) — never a fabricated
    // account id built out of the lane name.
    const subject = taskSubject(taskId) ??
      boxSubject(runnerId) ?? { kind: 'session' as const, id: ctx.row.sessionId, label: lane };
    return {
      line: noCredentialLine(reasonKind, lane),
      subject,
      refs: {
        ...(taskId !== undefined ? { taskId } : {}),
        ...(runnerId !== undefined ? { boxId: runnerId } : {}),
      },
    };
  },
};

const dispatchKeyCooled = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const taskId = str(input, 'taskId');
    const runId = str(input, 'runId');
    if (taskId === undefined) return null;
    const reason = str(input, 'reason') ?? 'unknown';
    return {
      line: keyCooledLine(reason, str(input, 'evidence'), str(input, 'providerCode')),
      subject: { kind: 'task', id: taskId, label: taskId },
      refs: { taskId, ...(runId !== undefined ? { runId } : {}) },
    };
  },
};

/**
 * `task.assigned` — BOTH writers (`apps/worker/src/wiring/nats-consumers.ts`,
 * `.../mothership.ts`) set `visibleToMaintainer: false` unconditionally, so
 * `row.input` is ALWAYS `null` here in production (see module header). There
 * is therefore no taskId/runnerId to read off this row; the only identifying
 * field the query does not gate is `repoFullName`, so that is the whole
 * render.
 */
const taskAssigned = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    if (ctx.row.repoFullName === null) return null;
    return {
      line: { key: 'terminal.task.assigned', params: { repo: ctx.row.repoFullName } },
      subject: { kind: 'repo', id: ctx.row.repoFullName, label: ctx.row.repoFullName },
      refs: { repoFullName: ctx.row.repoFullName },
    };
  },
};

/**
 * `task.held` (plan 102 slice 01) — a pending task was stamped with a
 * `hold_reason`. Reads `output` (`TaskHeldOutputSchema`, `../../audit/payload`).
 * `warn`: a held task is work not moving, and this line is why. `until: null`
 * renders `none` — a hold that lifts on a signal, not a window.
 */
const taskHeld = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const output = rec(ctx.row.output);
    const taskId = str(output, 'task_id');
    if (taskId === undefined) return null;
    return {
      line: {
        key: 'terminal.task.held',
        params: {
          reason: str(output, 'reason') ?? 'unknown',
          until: str(output, 'until') ?? 'none',
        },
      },
      subject: { kind: 'task', id: taskId, label: taskId },
      refs: { taskId },
    };
  },
};

// ─── runner.* box IDENTITY lifecycle (enroll through decommission) ──────────
// Every kind below is `subject.kind: 'box'`, `refs.boxId` set, `actor: 'system'`
// per the task brief. Payload shapes are `RunnerBoxAudit`
// (`apps/api/src/routes/runners.ts`) plus the presence-reaper writes in
// `apps/worker/src/wiring/mothership.ts`.

const runnerEnrolled = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    const hostname = str(input, 'hostname');
    return {
      line: {
        key: 'terminal.runner.enrolled',
        params: { ...(hostname !== undefined ? { hostname } : {}) },
      },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerStaleOffline = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    return {
      line: { key: 'terminal.runner.stale_offline', params: {} },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

// #4400 (Terminal slice 09): the symmetric `offline -> online` recovery
// `runner.stale_offline` never got. Payload shape is the reaper's own
// `onRevived` write (`apps/worker/src/wiring/mothership.ts`), which mirrors
// `runner.stale_offline`'s `{ runnerId }` exactly — the two are the same
// transition in opposite directions, so they read the payload the same way.
const runnerOnline = {
  level: 'ok' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    return {
      line: { key: 'terminal.runner.online', params: {} },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerRevoked = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    return {
      line: { key: 'terminal.runner.revoked', params: {} },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

// A pending enrollment whose token expired unused, deleted by the sweep. Same
// `{ runnerId }` payload as its lifecycle siblings; the id names a row that no
// longer exists, which is the point — this line is its only trace.
const runnerEnrollmentExpired = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    return {
      line: { key: 'terminal.runner.enrollment_expired', params: {} },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerDecommissioned = {
  level: 'decision' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    const reenqueued = num(input, 'reenqueued') ?? 0;
    return {
      line: { key: 'terminal.runner.decommissioned', params: { reenqueued } },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerHardeningReported = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    const rejected = str(input, 'hardeningRejected');
    return {
      line: {
        key: 'terminal.runner.hardening_reported',
        params: { ...(rejected !== undefined ? { rejected } : {}) },
      },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerCredsReissued = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    return {
      line: { key: 'terminal.runner.creds_reissued', params: {} },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerCredsRefreshReported = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    return {
      line: { key: 'terminal.runner.creds_refresh_reported', params: {} },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerAssignmentUndecodableReported = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    return {
      line: { key: 'terminal.runner.assignment_undecodable_reported', params: {} },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerModeChanged = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    const previousMode = str(input, 'previousMode') ?? 'unknown';
    const newMode = str(input, 'newMode') ?? 'unknown';
    return {
      line: {
        key: 'terminal.runner.mode_changed',
        params: { previousMode, newMode },
      },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

// `runner.capabilities_updated`: 'omit'. Corrected 2026-09-19 — the docblock this
// replaces claimed the presence-reaper's beat-driven merge writes
// `visibleToMaintainer: true`; measured against the actual writer
// (`wiring/mothership.ts`'s `onCapabilitiesUpdated`), it is `false`, same as the
// setup-lane advert (`wiring/nats-consumers.ts`). BOTH of this kind's writers are
// `false`, so `row.input`/`row.output` are ALWAYS null in production — a payload
// round-trip this renderer's `if (subject === null …) return null` guard hid
// rather than surfaced: an 'info' entry that could never once fire, defended by a
// comment whose own grep result was wrong.
//
// Unlike `task.assigned` (above), there is no fallback here: a box-lifecycle line
// needs a box id to be worth anything at all — a line with no `refs.boxId` cannot
// be linked to the box it is about — and `runnerId` lives ONLY in the withheld
// payload. `TerminalTailRow` carries no separate box/runner column a renderer
// could fall back to (unlike `repoFullName` for a repo-anchored kind). So the
// honest entry is 'omit', not a renderer that always returns null: 'omit' is a
// documented decision a reviewer can read; an always-null render is the same
// decision, made silently, wearing an 'info' label nobody will ever see fire.

const runnerBootRefusalReported = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    const version = str(input, 'version') ?? 'unknown';
    const reason = str(input, 'reason') ?? 'unknown';
    return {
      line: {
        key: 'terminal.runner.boot_refusal_reported',
        params: { version, reason },
      },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

/**
 * `runner.log_reported` — a pointer to a bounded log slice a box archived. The
 * pointer itself is never rendered as a link (no `logRef`-shaped `TerminalRefs`
 * field exists — deliberately, per the wire contract's route table), so the
 * line names only that a report landed and for what trigger.
 */
const runnerLogReported = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    const trigger = str(input, 'trigger') ?? 'unknown';
    return {
      line: { key: 'terminal.runner.log_reported', params: { trigger } },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

/**
 * This family's OWN entries, named and exported so a partition test can prove
 * this table and {@link TERMINAL_RENDERERS_DISPATCH_ROLLOUT} don't overlap.
 * The composed export below spreads both into ONE `terminalRendererGroup` call, so
 * a key declared in both is NOT a `ts(1117)` duplicate-key error the way one
 * object literal would be — the later spread (the rollout table) would just win
 * silently, and neither the whole-vocabulary partition test (which only ever sees
 * the composed whole, not which half a key came from) nor the total-coverage test
 * (the same count either way) could catch it.
 */
const DISPATCH_OWN = {
  [AuditEventKind.ExpectationRecorded]: expectationRecorded,
  [AuditEventKind.ExpectationChecked]: expectationChecked,
  [AuditEventKind.PlanRefused]: planRefused,
  [AuditEventKind.PlanGroupDropped]: planGroupDropped,
  [AuditEventKind.PlanExecuted]: planExecuteRenderer('info', 'executed'),
  [AuditEventKind.PlanExecuteRefused]: planExecuteRenderer('warn', 'execute_refused'),
  [AuditEventKind.PlanFileRequested]: planFileRequested,
  [AuditEventKind.PlanRevised]: planExecuteRenderer('info', 'revised'),
  [AuditEventKind.DispatchCreated]: dispatchCreated,
  [AuditEventKind.DispatchEscalated]: dispatchEscalated,
  [AuditEventKind.TriageVerdict]: triageVerdict,
  [AuditEventKind.TriageInfoAnswered]: triageInfoAnswered,
  [AuditEventKind.DispatchQueueFull]: dispatchQueueFull,
  [AuditEventKind.DispatchSeatCapped]: dispatchSeatCapped,
  [AuditEventKind.DispatchSeatCapNotified]: dispatchSeatCapNotified,
  [AuditEventKind.DispatchSeatCapCleared]: dispatchSeatCapCleared,
  [AuditEventKind.DispatchNoCredential]: dispatchNoCredential,
  [AuditEventKind.DispatchKeyCooled]: dispatchKeyCooled,
  // Platform-only (`visibleToMaintainer: false` at its one writer): it may name a key id
  // of another account, and nothing changed on this one.
  [AuditEventKind.DispatchKeyFailedRefused]: 'omit',
  // Advisory re-decision, not admission or task execution. Keep exact hold facts
  // in audit; the subsequent task assignment is the terminal-facing outcome.
  [AuditEventKind.DispatchKeyCapacityWoken]: 'omit',
  // #7105, reader-first: no writer this release. The line (and its `terminal.*` i18n key)
  // ships with the writer, when its payload exists to read; until then omit is honest.
  [AuditEventKind.QueueJudged]: 'omit',
  [AuditEventKind.QueueTaskRevived]: 'omit',
  // #6759, reader-first: no writer this release (#6762), so no payload to render; the line
  // and its `terminal.*` key ship with the writer, as `queue.judged` above.
  [AuditEventKind.ModelCallOffered]: 'omit',
  [AuditEventKind.ModelCallSettled]: 'omit',
  // The caller's drain, not a terminal outcome: the offer's settle row is the outcome.
  [AuditEventKind.ModelCallHandedBack]: 'omit',
  [AuditEventKind.DispatchModelEscalated]: dispatchModelEscalated,
  [AuditEventKind.TaskAssigned]: taskAssigned,
  [AuditEventKind.TaskHeld]: taskHeld,
  [AuditEventKind.RunnerEnrolled]: runnerEnrolled,
  [AuditEventKind.RunnerStaleOffline]: runnerStaleOffline,
  [AuditEventKind.RunnerOnline]: runnerOnline,
  [AuditEventKind.RunnerRevoked]: runnerRevoked,
  [AuditEventKind.RunnerEnrollmentExpired]: runnerEnrollmentExpired,
  [AuditEventKind.RunnerDecommissioned]: runnerDecommissioned,
  [AuditEventKind.RunnerHardeningReported]: runnerHardeningReported,
  [AuditEventKind.RunnerCredsReissued]: runnerCredsReissued,
  [AuditEventKind.RunnerCredsRefreshReported]: runnerCredsRefreshReported,
  [AuditEventKind.RunnerAssignmentUndecodableReported]: runnerAssignmentUndecodableReported,
  [AuditEventKind.RunnerModeChanged]: runnerModeChanged,
  [AuditEventKind.RunnerCapabilitiesUpdated]: 'omit',
  // Platform row (`apps/worker` relay, plan 101 02-2): `visibleToMaintainer: false` at
  // its one writer, about the transport rather than a tenant's work.
  [AuditEventKind.OutboxDeadLettered]: 'omit',
  [AuditEventKind.RunnerBootRefusalReported]: runnerBootRefusalReported,
  [AuditEventKind.RunnerLogReported]: runnerLogReported,
} as const;

/** For the partition test only — see the doc above {@link DISPATCH_OWN}. */
export function dispatchOwnKeys(): readonly string[] {
  return Object.keys(DISPATCH_OWN);
}

export const TERMINAL_RENDERERS_DISPATCH = terminalRendererGroup({
  ...DISPATCH_OWN,
  ...TERMINAL_RENDERERS_DISPATCH_ROLLOUT,
  ...ACCOUNT_VERBS_DISPATCH,
});
